import React, { useState, useEffect, useRef } from 'react';
import { toast } from '../../utils/notify';
import {
    CheckCircleIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import { getTermsDocumentApi, updateUserConsentApi } from '../../services/axios/AuthApis/UserMasterApis';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Document, Page } from 'react-pdf';
import * as pdfjs from 'pdfjs-dist';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const ConsentSettings = ({ user }) => {
    const viewerRef = useRef(null);
    const [userConsent, setUserConsentState] = useState(localStorage.getItem("userConsent") === "true");
    const { state } = useLocation();
    const { user: authUser } = useAuth();
    const [email, setEmail] = useState(authUser?.email || user?.email || state?.username || localStorage.getItem('pendingUserEmail'));
    const [termsDocumentUrl, setTermsDocumentUrl] = useState("");
    const [termsLoading, setTermsLoading] = useState(true);
    const [numPages, setNumPages] = useState(0);
    const [viewerWidth, setViewerWidth] = useState(900);

    const onDocumentLoadSuccess = ({ numPages: loadedPages }) => {
        setNumPages(loadedPages);
    };

    useEffect(() => {
        const loadTermsDocument = async () => {
            try {
                setTermsLoading(true);
                const response = await getTermsDocumentApi();
                setTermsDocumentUrl(response?.document_url || "");
            } catch (error) {
                setTermsDocumentUrl("");
            } finally {
                setTermsLoading(false);
            }
        };

        loadTermsDocument();
    }, []);

    useEffect(() => {
        const updateWidth = () => {
            if (!viewerRef.current) return;
            const nextWidth = Math.max(280, viewerRef.current.clientWidth - 32);
            setViewerWidth(nextWidth);
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    const handleConsentToggle = async () => {
        const newConsent = !userConsent;
        const payload = {
            email: authUser?.email || user?.email || state?.username || localStorage.getItem('pendingUserEmail') || email,
            consent_given: newConsent
        }
        const response = await updateUserConsentApi(payload);
        setUserConsentState(newConsent);
        localStorage.setItem("userConsent", newConsent.toString());
        window.dispatchEvent(new CustomEvent("consentChanged"));
        toast.success(`Consent ${newConsent ? "given" : "revoked"} successfully`);
    };

    return (
        <div className="space-y-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg flex-shrink-0">
                        <CheckCircleIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">User Consent Management</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Control your consent status. When consent is revoked, you'll only have access to the Dashboard.
                        </p>
                    </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 md:p-6 mb-6 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Current Consent Status</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {userConsent ? "You have given consent to access all platform features" : "You have not given consent. Only Dashboard is accessible"}
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            <button
                                onClick={handleConsentToggle}
                                className={`w-full py-2 px-5 rounded-md font-semibold flex items-center justify-center gap-2 transition duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${userConsent
                                    ? "bg-red-500 hover:bg-red-600 text-white focus:ring-red-300"
                                    : "bg-green-500 hover:bg-green-600 text-white focus:ring-green-300"
                                    }`}
                            >
                                {userConsent ? <XCircleIcon className="w-5 h-5" /> : <CheckCircleIcon className="w-5 h-5" />}
                                <span>{userConsent ? "Revoke Consent" : "Give Consent"}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <p className="text-xs md:text-sm text-yellow-800 dark:text-yellow-300">
                        <strong>Note:</strong> When you revoke consent, you'll be restricted to Dashboard access only. You'll need to give consent again to access other features.
                    </p>
                </div>

                <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Terms of Service</h3>
                    {termsLoading ? (
                        <div className="text-sm text-gray-600 dark:text-gray-400">Loading document...</div>
                    ) : termsDocumentUrl ? (
                        <div className="space-y-2">
                            <div
                                ref={viewerRef}
                                className="h-[600px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/30"
                            >
                                <div className="p-4 space-y-4">
                                    <Document
                                        file={termsDocumentUrl}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        loading={<div className="text-sm text-gray-600 dark:text-gray-400">Rendering document...</div>}
                                        error={<div className="text-sm text-red-600 dark:text-red-400">Unable to render Terms of Service document.</div>}
                                    >
                                        {Array.from(new Array(numPages), (_, index) => (
                                            <div key={`settings_page_${index + 1}`} className="mb-4 flex justify-center">
                                                <Page pageNumber={index + 1} width={viewerWidth} />
                                            </div>
                                        ))}
                                    </Document>
                                </div>
                            </div>
                            <a
                                href={termsDocumentUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                Open original document
                            </a>
                        </div>
                    ) : (
                        <div className="text-sm text-red-600 dark:text-red-400">Unable to load Terms of Service document.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConsentSettings;
